/**
 * Offline sync queue types.
 *
 * @author Milton Filho
 */

export enum OfflineActionKind {
  StartSession = 'StartSession',
  BookAppointment = 'BookAppointment',
}

export interface OfflineQueueItem {
  /** Unique identifier for this queue item */
  id: string;
  /** The kind of offline action queued */
  kind: OfflineActionKind;
  /** Original API payload to replay on sync */
  payload: Record<string, unknown>;
  /** Temp appointment IDs created for optimistic updates */
  tempAppointmentIds: number[];
  /** ISO timestamp of when the item was enqueued */
  createdAt: string;
  /** ISO timestamp of when the item failed to sync (if applicable) */
  failedAt?: string;
}

export interface OfflineSyncState {
  /** Ordered queue of items pending sync */
  queue: OfflineQueueItem[];
  /** Appointment IDs currently pending sync (positive for arrive, negative for bookings) */
  pendingIds: number[];
  /** Decrementing counter for generating temp negative IDs */
  tempIdCounter: number;
  /** Current sync status */
  syncStatus: 'idle' | 'syncing';
}

/**
 * What an optimistic appointment card is waiting on, if anything.
 *
 * The two differ in whether anything is still *going* to happen:
 *
 * - `pending` — a queue item still references this appointment, so a reconnect
 *   will replay it. The wait is real and it ends.
 * - `unconfirmed` — the id is still marked, but no queue item references it any
 *   more. Nothing will replay it, ever. This is where a `409` on a booking that
 *   had already failed once lands: the appointment probably does exist in
 *   Clinicient, our schedule cannot see it, and the card is the only trace of
 *   it — so it stays, but calling it "pending" would promise a sync that is
 *   never coming.
 *
 * Derived, not stored. Both facts are already in `offlineSync`, so this needs no
 *   new field — which matters because the whole redux state is persisted to
 *   `localStorage` with no schema version, and a state written by an earlier
 *   build has to keep reading correctly.
 */
export type AppointmentSyncMark = 'pending' | 'unconfirmed';
